/**
 * The AGC stage: loads the `avgv-dsp` WASM into the AudioWorklet.
 *
 * @module
 */
import workletUrl from "./agc.worklet.ts?worker&url";
import wasmUrl from "./dsp/avgv_dsp_wasm_bg.wasm?url";
import {
  AGC_PROCESSOR,
  type AgcMessage,
  type AgcPresetName,
  type AgcProcessorOptions,
  type AgcReport,
} from "./agc-protocol";

export type { AgcPresetName };

export interface AgcNode {
  readonly node: AudioWorkletNode;
  setEnabled(enabled: boolean): void;
  setPreset(preset: AgcPresetName): void;
  setTarget(dbfs: number): void;
  /** The gain the AGC is applying now, in dB; 0 when off. */
  readonly gainDb: number;
}

const loaded = new WeakMap<BaseAudioContext, Promise<void>>();
let wasmBytes: Promise<ArrayBuffer> | undefined;

function loadWasm(): Promise<ArrayBuffer> {
  wasmBytes ??= fetch(wasmUrl).then((r) => {
    if (!r.ok) throw new Error(`AGC module: HTTP ${r.status}`);
    return r.arrayBuffer();
  });
  return wasmBytes;
}

/** Creates the AGC stage (design §7.4.1). Stereo by default. */
export async function createAgcNode(ctx: BaseAudioContext, channels = 2): Promise<AgcNode> {
  let ready = loaded.get(ctx);
  if (!ready) {
    ready = ctx.audioWorklet.addModule(workletUrl);
    loaded.set(ctx, ready);
  }
  const [wasm] = await Promise.all([loadWasm(), ready]);
  const processorOptions: AgcProcessorOptions = { wasm, channels };
  const node = new AudioWorkletNode(ctx, AGC_PROCESSOR, {
    numberOfInputs: 1,
    numberOfOutputs: 1,
    channelCount: channels,
    channelCountMode: "explicit",
    outputChannelCount: [channels],
    processorOptions,
  });
  const send = (msg: AgcMessage) => node.port.postMessage(msg);
  let gainDb = 0;
  let enabled = true;
  node.port.onmessage = (e: MessageEvent<AgcReport>) => {
    gainDb = e.data.db;
  };
  return {
    node,
    setEnabled: (on) => {
      enabled = on;
      send({ type: "enabled", enabled: on });
    },
    setPreset: (preset) => send({ type: "preset", preset }),
    setTarget: (dbfs) => send({ type: "target", dbfs }),
    get gainDb() {
      return enabled ? gainDb : 0;
    },
  };
}
